import type { TextJson } from 'contracts/tess';
import { Object3D, Mesh, MeshBasicMaterial, Matrix4, Vector3 } from 'three';
import type {
  Box3,
  DepthModes,
  Float32BufferAttribute,
  Group,
  Intersection,
  Raycaster,
} from 'three';
import { Text } from 'troika-three-text';
import type { GlyphsGeometry, TroikaTextMaterial } from 'troika-three-text';

// eslint-disable-next-line no-restricted-imports
import { getOrCreateThreeColor } from '../../colors';
// eslint-disable-next-line no-restricted-imports
import type { ColorChoice } from '../../colors';
// eslint-disable-next-line no-restricted-imports
import { disposeThreeJS } from '../../geom-utils';
// eslint-disable-next-line no-restricted-imports
import { Font } from '../../renderer/addons/Font';
// eslint-disable-next-line no-restricted-imports
import { TextGeometry } from '../../renderer/addons/TextGeometry';
// eslint-disable-next-line no-restricted-imports
import type { Intersectable } from '../../renderer/renderer';
import type { STEPBody } from './STEPBody';
import { defaultTextColor } from './STEPBody';
import type { TextData } from './STEPGeometry';
import type { Style } from './StepStyleAdapter';
import { StepStyleAdapter } from './StepStyleAdapter';
import './troikaThreeText.d';

/*
 * StepTextModel provides some metadata about the text
 * @remarks StepTextModel extends Object3D and overrides the raycast() method so it can be used to calculate this text's intersection by a Raycaster
 */
export class StepTextModel extends Object3D implements Intersectable {
  public static font: Font;

  public readonly isLineSegments = false; //See Line.prototype.raycast implementation (must be false to iterate over drawRange as expected)
  public readonly metaData: Required<TextJson>['meta'];
  public readonly index: number;
  public readonly text: string;
  private _style?: Style;
  public readonly parent: Group; //The parent group for the body's texts this text belongs to
  public material: MeshBasicMaterial | TroikaTextMaterial;

  /**
   * @remarks: boundBox is transformed in context of its body, whereas this.geometry.boundBox is NOT transformed!
   * this._boundBox is used by intersection-utils.ts to test if frustrum intersects this box
   */
  private _boundBox?: Box3;
  private _geometry?: TextGeometry | GlyphsGeometry;
  private _mesh?: Mesh<TextGeometry, MeshBasicMaterial> | Text;
  private loadedPromise?: Promise<this>;
  private isDisposed?: boolean;

  constructor(
    public readonly name: string,
    data: TextData,
    public readonly host: STEPBody,
  ) {
    super();
    this.metaData = { ...data.meta }; //boundBox and boundSphere removed from metaData
    this.index = data.meta.index;
    this.text = data.text;

    this.parent = this.host.textsGroup;

    this.material = new MeshBasicMaterial({
      color: this.metaData?.color ?? defaultTextColor,
    });

    this.load(data);

    //this.style is an adapter view into metaData
    this._style = new StepStyleAdapter(this, this.host);
  }

  public get style(): Style {
    if (!this._style) {
      throw new Error('This object was disposed');
    }
    return this._style;
  }

  public get rootBodyOrAssemblyInstanceId() {
    return this.host.body.rootBodyOrAssemblyInstanceId;
  }

  public get mesh() {
    return this._mesh as Mesh<TextGeometry, MeshBasicMaterial> | Text;
  }

  /**
   * @remarks: boundBox is transformed in context of its body, whereas this.geometry.boundBox is NOT transformed!
   * this.boundBox is used by intersection-utils.ts to test if frustrum intersects this box
   */
  public get boundBox(): Box3 {
    return this._boundBox as Box3;
  }

  public get geometry(): TextGeometry | GlyphsGeometry {
    return this._geometry as TextGeometry | GlyphsGeometry;
  }

  //rendered promise: Resolves when the text is finished first sync() (it's loaded)
  public get loaded(): Promise<this> {
    return this.loadedPromise ?? Promise.resolve(this);
  }

  public load({
    text,
    textAlign,
    size,
    rectWidth,
    anchorX,
    anchorY,
    transform,
  }: TextJson) {
    //Was using `textAlign` as sniff test for `mtext`.
    //Now `text` is rendered the same way. So the TextGeometry case can be deprecated. GlyphsGeometry is better (less memory and better visual quality)
    if (textAlign) {
      const textEnt: Text = new Text();
      //In textEnt.sync(), textEnt.material will be instantiated with this.material as the baseMaterial and replace textEnt.material
      textEnt.material = this.material as TroikaTextMaterial;
      textEnt.text = text;
      textEnt.color = this.metaData?.color ?? defaultTextColor;
      //font fallsback to Roboto from google fonts
      //TODO: translate data.font to a font to load and have a whitelist of fonts
      //if (data.font) {
      //  textEnt.font = data.font;
      //}
      textEnt.fontSize = size || 12;
      if (rectWidth) {
        textEnt.maxWidth = rectWidth;
      }

      textEnt.anchorX = anchorX ?? 'left';
      textEnt.anchorY = anchorY ?? 'bottom';
      textEnt.textAlign = textAlign;

      if (transform) {
        //Must be set before textEnt.sync(), and is set via position, quaternion and scale properties
        new Matrix4()
          .set(...transform)
          .decompose(textEnt.position, textEnt.quaternion, textEnt.scale);
      }

      this.loadedPromise = new Promise<void>((resolve, reject) => {
        textEnt.sync(() => {
          try {
            const geometry = textEnt.geometry;
            this._mesh = textEnt;
            this._geometry = geometry;
            this.material = textEnt.material; //set again because textEnt.sync() replaces the material
            geometry.computeBoundingBox();
            geometry.computeBoundingSphere();
            this._boundBox = (geometry.boundingBox as Box3).clone();
            //Note: this._boundBox is not in the correct location yet.  So in body.ts, finalize() updates the matrixWorld in each StepTextModel
            //And then transforms the text.boundBox into the correct location.

            const { max, min } = this._boundBox;

            if (textEnt.textAlign !== 'left') {
              const textWidth = max.x - min.x;
              if (rectWidth != null) {
                textEnt.position.x +=
                  textEnt.textAlign === 'center'
                    ? (rectWidth - textWidth) / 2
                    : textEnt.textAlign === 'right'
                    ? rectWidth - textWidth
                    : textEnt.position.x;
              }
            }

            //This works in tuning some cases, but doesn't make sense. The 1/4 factor is just coincidence
            //More information is needed to make the alignment adjustments
            //if (textEnt.anchorY === 'top') {
            //  const textHeight = max.y - min.y;
            //  textEnt.position.y -= textHeight / 4;
            //}

            this.parent.add(this._mesh);
            resolve();
          } catch (e) {
            reject(e);
          }
        });
      }).then(() => {
        //If this.firstSyncPromise rejects, keep this.firstSyncPromise, otherwise cleanup cached firstSyncPromise
        delete this.loadedPromise;
        return Promise.resolve(this);
      });
    } else {
      this.loadedPromise = (async () => {
        await StepTextModel.loadFont();
        this._geometry = new TextGeometry(text, {
          font: StepTextModel.font,
          height: 0,
          size: size || 12,
        });
        if (transform) {
          this._geometry.applyMatrix4(new Matrix4().set(...transform));
        }
        this._geometry.computeBoundingBox();
        this._geometry.computeBoundingSphere();

        //cloned because this._boundBox will be transformed in context of its body
        //do not confuse this with this._geometry.boundBox which is not transformed
        //this._geometry.computeBoundingBox();
        this._boundBox = (this._geometry.boundingBox as Box3).clone();

        this._mesh = new Mesh(this._geometry, this.material);

        this.parent.add(this._mesh);

        delete this.loadedPromise;
        return this;
      })();
    }
  }

  public dispose() {
    if (this.isDisposed) return;
    this.isDisposed = true;
    disposeThreeJS(this._mesh, this._geometry);
    delete this._style;
  }

  //loadFont() is used to load font on demand because StepTextModel is often never used
  public static async loadFont() {
    if (!StepTextModel.font) {
      const json = JSON.parse(
        JSON.stringify(
          await import(
            '../../renderer/addons/helvetiker_regular.typeface.json'
          ),
        ),
      );
      StepTextModel.font = new Font(json);
    }
  }

  public get partUuid() {
    return this.host.partUuid;
  }

  public get parentName() {
    return this.host.name;
  }

  public get isMaterial(): boolean | undefined {
    return this.host.isMaterial;
  }

  public get transform() {
    return this.host.transform;
  }

  public get body() {
    return this.host.body;
  }

  /* eslint-disable @typescript-eslint/ban-ts-comment */

  //Note: this.visible is deleted after super() is called. It will be replaced by this getter/setter pair
  //@ts-ignore
  public get visible(): boolean {
    //Text's visibility is controlled by the parent's visibility and the opacity
    return this.parent.visible && !!this._mesh?.visible;
  }

  //@ts-ignore
  public set visible(v: boolean) {
    //Text's visibility is controlled by the parent's visibility and the opacity
    //But when using the setter, we only modify the parent's visibility (which impacts ALL sibling text)
    if (this._mesh) {
      this._mesh.visible = v;
      if (v && this.parent && !this.parent.visible) {
        this.parent.visible = v;
      }
    }
  }

  /**
   * @remarks: Text.prototype.raycast requires this.matrixWorld
   */
  //@ts-ignore
  public get matrixWorld() {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this._mesh!.matrixWorld;
  }

  //@ts-ignore
  public set matrixWorld(v: Matrix4) {
    if (this._mesh) {
      this._mesh.matrixWorld = v;
    }
  }

  public get depthFunc(): DepthModes {
    return this.material.depthFunc;
  }

  public set depthFunc(v: DepthModes) {
    this.material.depthFunc = v;
  }

  public get opacity(): number {
    return this.material.opacity;
  }

  public set opacity(v: number) {
    this.material.opacity = v;
  }

  public get defaultColor() {
    return defaultTextColor;
  }

  //color rgb components in range of 0..1
  public get color() {
    return this.material.color;
  }

  public setColor(color: ColorChoice) {
    const c = getOrCreateThreeColor(color);
    this.material.color = c;
    if (this._mesh instanceof Text) {
      this._mesh.color = c;
    }
  }

  public *positions() {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const geometry = this._geometry!;
    const { start, count } = geometry.drawRange;
    const end = start + count * 3;
    for (let i = start; i < end; i++) {
      yield (geometry.attributes.position as Float32BufferAttribute)?.array[i];
    }
  }

  public translate(v: Vector3) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this._boundBox!.translate(v);
  }

  /**
   * @remarks: this.raycast requires this.getVertexPosition() as of https://github.com/mrdoob/three.js/releases/tag/r148. Currently not defined in threejs types, but it is here https://threejs.org/docs/#api/en/objects/Mesh
   */
  public getVertexPosition(index: number, target: Vector3): Vector3 {
    return Mesh.prototype.getVertexPosition.call(this, index, target);
  }

  /**
   * @remarks: this.raycast requires this.textRenderInfo
   */
  public get textRenderInfo() {
    if (this._mesh instanceof Text) {
      return this._mesh.textRenderInfo;
    }
  }

  /**
   * @remarks: this.raycast requires this.curveRadius
   */
  public get curveRadius() {
    if (this._mesh instanceof Text) {
      return this._mesh.curveRadius;
    }
  }

  public raycast(raycaster: Raycaster, intersects: Intersection[]): void {
    //Don't use this._mesh.raycast(raycaster, intersects), because we need `this.name` downstream for matching
    if (this._mesh instanceof Text) {
      Text.prototype.raycast.call(this, raycaster, intersects);
    } else {
      Mesh.prototype.raycast.call(this, raycaster, intersects);
    }
  }
}
